import * as React from 'react'
import { Check, Loader2, Trash2 } from 'lucide-react'
import {
  ALLOWED_LOGO_CONTENT_TYPES,
  MAX_LOGO_BYTES,
} from '@lib/constants/documents'
import type { OrganizationDto } from '@lib/dto/organization.dto'
import { updateOrganizationInputSchema } from '@lib/input/organization.input'
import {
  useOrganizationUpdatePending,
  useUpdateOrganization,
} from '@/hooks/useOrganization'
import { UploadButton } from '@/components/UploadButton'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { errorMessage } from '@/helpers/logger'

/**
 * The number clients are told to call.
 *
 * Saved on its own, like the name, because it is one line somebody types
 * once. Empty clears it, and a proposal then shows no number rather than an
 * empty "Call" button.
 */
function PhoneField({ phone }: { phone: string | null }) {
  const updateOrganization = useUpdateOrganization()
  const saving = useOrganizationUpdatePending()
  const [draft, setDraft] = React.useState<string>(phone ?? '')
  const [error, setError] = React.useState<string | null>(null)
  const [saved, setSaved] = React.useState<boolean>(false)

  const isDirty = draft.trim() !== (phone ?? '')

  async function save(): Promise<void> {
    setError(null)
    setSaved(false)
    const parsed = updateOrganizationInputSchema
      .pick({ phone: true })
      .safeParse({ phone: draft })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Enter a phone number.')
      return
    }
    try {
      await updateOrganization.mutateAsync({ phone: parsed.data.phone ?? null })
      setSaved(true)
    } catch (caught) {
      setError(errorMessage(caught, 'That could not be saved.'))
    }
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor="organization-phone">Phone</Label>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          id="organization-phone"
          className="max-w-xs"
          inputMode="tel"
          placeholder="(806) 794-1300"
          value={draft}
          onChange={(event) => {
            setSaved(false)
            setDraft(event.target.value)
          }}
        />
        {isDirty ? (
          <Button
            size="sm"
            disabled={saving}
            onClick={() => {
              void save()
            }}
          >
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : null}
            Save phone
          </Button>
        ) : null}
        {saved && !isDirty ? (
          <span className="flex items-center gap-1 text-sm text-muted-text">
            <Check className="h-3.5 w-3.5" />
            Saved
          </span>
        ) : null}
      </div>
      {error === null ? (
        <p className="text-xs text-muted-text">
          Shown on proposals beside the Accept button.
        </p>
      ) : (
        <p className="text-xs text-danger">{error}</p>
      )}
    </div>
  )
}

/**
 * Who the business is: its name, and the logo printed beside it.
 *
 * Applies on upload rather than behind a Save button, unlike the other
 * settings sections. Picking a file IS the decision here — there is no second
 * field to get consistent with it first, and a chosen logo sitting unsaved
 * beside a preview of itself would only invite someone to navigate away
 * believing it was set.
 */
export function OrganizationIdentitySettings({
  organization,
}: {
  organization: OrganizationDto
}) {
  const updateOrganization = useUpdateOrganization()
  const saving = useOrganizationUpdatePending()
  const [error, setError] = React.useState<string | null>(null)
  const [logoUploading, setLogoUploading] = React.useState(false)
  const [name, setName] = React.useState<string>(organization.name)
  const [savedName, setSavedName] = React.useState<boolean>(false)

  const trimmedName = name.trim()
  const nameIsDirty = trimmedName !== organization.name
  const canSaveName = nameIsDirty && trimmedName !== ''

  async function saveName(): Promise<void> {
    setError(null)
    setSavedName(false)
    try {
      await updateOrganization.mutateAsync({ name: trimmedName })
      setSavedName(true)
    } catch (caught) {
      setError(errorMessage(caught, 'That could not be saved.'))
    }
  }

  async function setLogo(logoDocumentId: string | null): Promise<void> {
    setError(null)
    try {
      await updateOrganization.mutateAsync({ logoDocumentId })
    } catch (caught) {
      setError(errorMessage(caught, 'That could not be saved.'))
    }
  }

  const hasLogo = organization.logoUrl !== null

  return (
    <section className="space-y-3 rounded-lg border border-default bg-surface p-4">
      <div>
        <h2 className="text-lg font-semibold">Business</h2>
        <p className="mt-0.5 text-sm text-muted-text">
          What clients see: the name on every proposal and in the subject line
          of mail you send, and the logo that heads the page.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="organization-name">Business name</Label>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            id="organization-name"
            className="max-w-xs"
            value={name}
            onChange={(event) => {
              setSavedName(false)
              setName(event.target.value)
            }}
          />
          {/*
            Appears only once there is something to save. A Save button that is
            disabled almost all the time is furniture, and this sits beside a
            logo control that needs no such step.
          */}
          {nameIsDirty ? (
            <Button
              size="sm"
              disabled={!canSaveName || saving}
              onClick={() => {
                void saveName()
              }}
            >
              {saving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : null}
              Save name
            </Button>
          ) : null}
          {savedName && !nameIsDirty ? (
            <span className="flex items-center gap-1 text-sm text-muted-text">
              <Check className="h-3.5 w-3.5" />
              Saved
            </span>
          ) : null}
        </div>
        {canSaveName || !nameIsDirty ? null : (
          <p className="text-xs text-danger">Enter the business name.</p>
        )}
      </div>

      <PhoneField phone={organization.phone} />

      <div className="space-y-1.5">
        <Label>Logo</Label>
        <p className="text-xs text-muted-text">
          Printed at the top of every proposal, in place of the name. PNG, JPEG
          or WebP, up to 2 MB — a wide image works best. Applies as soon as you
          pick it.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        {/*
          A checked background rather than white: most logos are supplied as a
          transparent PNG, and on a white card a white-on-transparent mark is
          invisible — which somebody would read as a failed upload.
        */}
        <div className="flex h-20 w-48 shrink-0 items-center justify-center rounded-md border border-default bg-subtle p-2">
          {hasLogo && organization.logoUrl !== null ? (
            <img
              src={organization.logoUrl}
              alt="The organization logo"
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <span className="text-xs text-muted-text">No logo set</span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <UploadButton
            meta={{ kind: 'LOGO' }}
            accept={ALLOWED_LOGO_CONTENT_TYPES}
            maxBytes={MAX_LOGO_BYTES}
            label={hasLogo ? 'Replace logo' : 'Upload logo'}
            disabled={saving}
            onUploadingChange={setLogoUploading}
            onUploaded={(document) => {
              void setLogo(document.id)
            }}
          />
          {hasLogo ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={saving || logoUploading}
              onClick={() => {
                void setLogo(null)
              }}
            >
              {saving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" />
              )}
              Remove
            </Button>
          ) : null}
        </div>
      </div>

      {error !== null ? <p className="text-sm text-danger">{error}</p> : null}

      {/*
        Said plainly, because the opposite would be the surprising behavior:
        everything else on a sent proposal is frozen, and somebody replacing a
        logo would reasonably worry they had just rewritten the letterhead on
        documents already with clients.
      */}
      <p className="rounded-md border border-default bg-subtle px-3 py-2 text-xs text-secondary-text">
        Changes affect proposals built from here on. A proposal that has been
        sent keeps the name, phone and logo it was sent with.
      </p>
    </section>
  )
}
